import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/synapse/Logo';
import { ThemeToggle } from '@/components/synapse/ThemeToggle';
import { LanguageToggle } from '@/components/synapse/LanguageToggle';
import { useAuth } from '@/hooks/useAuth';
import { CLINIC_PICKER_PATH } from '@/routing/tenant';
import { cn } from '@/lib/utils';

const TABS = [
  { to: '/admin/clinics', labelKey: 'platformAdmin.tabs.clinics' },
  { to: '/admin/passwords', labelKey: 'platformAdmin.tabs.passwords' },
  { to: '/admin/super-admins', labelKey: 'platformAdmin.tabs.superAdmins' },
] as const;

/**
 * The shell for `/admin`.
 *
 * Deliberately NOT `AppShell`. That shell's navigation is derived from the
 * caller's permissions inside one clinic, and none of those exist here — a
 * platform operator may not belong to a single clinic. Reusing it would mean
 * teaching it to render with no active clinic, which is a second mode in the
 * component every staff screen depends on.
 *
 * "Back to clinics" rather than a sign-out-only header: a super admin is an
 * ordinary user too, and the way out of the platform surface is the same picker
 * they came in from.
 */
export function PlatformAdminLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  return (
    <div className="flex min-h-screen w-full flex-col bg-background text-foreground">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-paper px-4 md:px-6">
        <Logo size={22} />
        <span className="text-[13px] font-medium tracking-[-0.01em] text-muted">
          {t('platformAdmin.title')}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden text-[12px] text-muted sm:inline">{user?.email}</span>
          <ThemeToggle />
          <LanguageToggle />
          <a
            href={CLINIC_PICKER_PATH}
            className="rounded-md px-2 py-1 text-[13px] text-muted hover:text-ink"
          >
            {t('platformAdmin.backToClinics')}
          </a>
          <Button variant="ghost" size="sm" onClick={() => logout({ redirect: true })}>
            {t('common.signOut')}
          </Button>
        </div>
      </header>

      <nav className="flex shrink-0 items-center gap-1 border-b border-line bg-paper px-4 md:px-6">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                '-mb-px border-b-2 px-3 py-2.5 text-[13px] transition-colors',
                isActive
                  ? 'border-ink font-medium text-ink'
                  : 'border-transparent text-muted hover:text-ink',
              )
            }
          >
            {t(tab.labelKey)}
          </NavLink>
        ))}
      </nav>

      <main className="mx-auto w-full max-w-[1180px] flex-1 px-4 py-6 md:px-6 md:py-8">
        <Outlet />
      </main>
    </div>
  );
}
